/**
 * IdentityTenantDetailPageV2 — Phase 6G (P0-IDENT).
 *
 * Per-tenant identity-configuration detail page. Reached by clicking a
 * row in the Tenant inheritance pane (sibling /identity/tenants list
 * view). Closes the dead-end regression where row clicks navigated to
 * /identity/tenants/:id with no matching route.
 *
 * Layout
 *   Tenant overview strip — agent count, env, override count
 *   Six dimension cards — one per setting in the inheritance matrix:
 *     trustDomain, svidTtl, rotation, spire, compliance, federation.
 *     Each card shows the resolved value side-by-side with the Org
 *     default so the user can see what's inherited vs overridden and
 *     at what value the override lands.
 *
 * Data
 *   Real fetch against GET /api/v1/identity/tenants/{tenantId}.
 *   Backend returns { id, name, agentCount, env, hasOverride,
 *                     orgDefaults, dimensions, cells } shape.
 *   404 path honoured for unknown tenant ids.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Users, ArrowUp, Pencil, AlertTriangle, Building2, Network,
  Clock, RefreshCw, Server, Scale, Share2, Box, ChevronRight,
} from 'lucide-react';
import { V2PageShell } from '../components/v2/V2PageShell';
import { Card } from '../components/v2/Card';
import { Button } from '../components/v2/Button';
import { Pill } from '../components/v2/Pill';
import { EmptyState } from '../components/v2/EmptyState';
import { Skeleton } from '../components/v2/Skeleton';
import { TenantOverrideEditSlideout } from '../components/v2/identity/TenantOverrideEditSlideout';
import { getIdentityTenant } from '../services/api';

/* Dimension → icon + tone map. Lifted to module scope so each page
 * render reuses the same references instead of recreating the map.
 * Static keys keep Tailwind's JIT happy with the icon-color classes
 * via the Pill's tone system (no dynamic concat). */
const DIMENSION_META = {
  trustDomain: { icon: Network,   iconTone: 'text-v2-action'  },
  svidTtl:     { icon: Clock,     iconTone: 'text-v2-warning' },
  rotation:    { icon: RefreshCw, iconTone: 'text-v2-warning' },
  spire:       { icon: Server,    iconTone: 'text-v2-trust'   },
  compliance:  { icon: Scale,     iconTone: 'text-v2-action'  },
  federation:  { icon: Share2,    iconTone: 'text-v2-success' },
};

/* ────────────────────────────────────────────────────────────────
   IdentityTenantDetailPageV2 — top-level export
   ──────────────────────────────────────────────────────────────── */

export default function IdentityTenantDetailPageV2() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /* Stage 3c-1 — page-level edit slideout. Single state object covers
   * both open/closed and the mode (add vs remove). Per-dimension
   * shortcuts are a follow-up PR (would attach onAction props to the
   * DimensionCard footer buttons). */
  const [editSlideout, setEditSlideout] = useState({ open: false, mode: 'add' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // apiFetch wraps responses as {ok, data} on success and
    // {ok: false, error} on error — see IdentityAgentDetailPageV2 for
    // the full contract note. Status discrimination via message text
    // because apiFetch doesn't expose HTTP status directly.
    const classify = (msg) => {
      let status = null;
      const m = /^HTTP (\d{3}):/.exec(msg);
      if (m) status = Number(m[1]);
      else if (/tenant_not_found/.test(msg)) status = 404;
      return { message: msg, status };
    };
    getIdentityTenant(tenantId)
      .then((res) => {
        if (cancelled) return;
        if (res?.ok === false) {
          setError(classify(res.error || 'Identity API error'));
        } else {
          setData(res?.data || null);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(classify(err?.message || String(err)));
          setLoading(false);
        }
      });
  }, [tenantId]);

  if (loading) {
    return (
      <V2PageShell title="Loading…" backTo="/identity/tenants" backLabel="Back to Tenants">
        <Skeleton height={80} />
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Skeleton height={140} /><Skeleton height={140} />
          <Skeleton height={140} /><Skeleton height={140} />
        </div>
      </V2PageShell>
    );
  }

  if (error) {
    const is404 = error.status === 404;
    return (
      <V2PageShell
        title={is404 ? 'Tenant not found' : 'Could not load tenant'}
        backTo="/identity/tenants"
        backLabel="Back to Tenants"
      >
        <Card>
          <EmptyState
            icon={<AlertTriangle />}
            title={is404 ? 'No tenant with that id' : 'Identity API error'}
            description={is404
              ? `The identifier "${tenantId}" is not in the inheritance matrix. It may have been removed, or the link was hand-edited.`
              : error.message}
            primaryAction={
              <Button variant="primary" size="sm" onClick={() => navigate('/identity/tenants')}>
                Back to Tenants
              </Button>
            }
          />
        </Card>
      </V2PageShell>
    );
  }

  // Count override cells for the overview strip — what the user sees in
  // the pane row's "override" pill should match what they see here.
  const cells = data.cells || {};
  const overrideCount = Object.values(cells)
    .filter((c) => c && c.inherit === false).length;

  return (
    <V2PageShell
      title={data.name}
      subtitle={`${data.agentCount} agents · ${data.env} · ${
        overrideCount === 0 ? 'fully inherits Org defaults' :
        overrideCount === 1 ? '1 setting overrides Org' :
        `${overrideCount} settings override Org`
      }`}
      backTo="/identity/tenants"
      backLabel="Back to Tenants"
      tags={
        <>
          <Pill tone="trust"  size="sm">Tenant</Pill>
          <Pill tone="action" size="sm" dot>SPIFFE plane</Pill>
          {data.hasOverride && <Pill tone="warning" size="sm">override</Pill>}
        </>
      }
      actions={
        <>
          {data.hasOverride && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditSlideout({ open: true, mode: 'remove' })}
            >
              Remove overrides
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={() => setEditSlideout({ open: true, mode: 'add' })}
          >
            {data.hasOverride ? 'Edit overrides' : 'Install override'}
          </Button>
        </>
      }
    >
      {/* Scope chain banner — mirrors the inheritance pane's banner so
       * the visual language stays consistent across pages. */}
      <Card padding="sm" className="mb-4">
        <div className="flex items-center gap-3">
          <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold">
            Scope chain
          </div>
          <Pill tone="action" size="sm">
            <Building2 size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />
            Organization
          </Pill>
          <span className="text-v2-ink-muted">›</span>
          <Pill tone="trust" size="sm">
            <Users size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />
            {data.name}
          </Pill>
          <div className="text-[11px] text-v2-ink-secondary ml-auto">
            Each setting inherits from Organization unless overridden here
          </div>
        </div>
      </Card>

      {/* Dimension cards — six, in a 2x3 grid */}
      <div className="grid grid-cols-2 gap-3">
        {(data.dimensions || []).map((dim) => (
          <DimensionCard
            key={dim.key}
            dimensionKey={dim.key}
            label={dim.label}
            cell={cells[dim.key]}
            orgDefault={data.orgDefaults?.[dim.key]}
          />
        ))}
      </div>

      {/* Stage 5-6 — Instances section. Shows the tenant's real
          instances from the canonical Multi-Tenancy table; each row
          is a click-through to /identity/instances/:id. Rotation
          policy + SPIRE binding live at instance scope per design
          §3.3 of IDENTITY_TENANCY_ALIGNMENT_ANALYSIS.md — so this is
          where admins land to install per-instance overrides. */}
      <Card className="mt-4">
        <Card.Header
          title={<><Box size={16} className="inline -mt-0.5 mr-1.5 text-v2-success" aria-hidden="true" />Instances</>}
          subtitle="Per-instance identity configuration lives one click away"
          actions={
            <Pill tone="muted" size="sm">
              {(data.instances || []).length} {(data.instances || []).length === 1 ? 'instance' : 'instances'}
            </Pill>
          }
        />
        <Card.Body>
          {(data.instances || []).length === 0 ? (
            <div className="text-[12.5px] text-v2-ink-muted py-3 text-center">
              No instances configured for this tenant yet. Create instances in the
              Multi-Tenancy section to manage per-environment identity policy.
            </div>
          ) : (
            <div className="flex flex-col divide-y divide-v2-border-subtle">
              {(data.instances || []).map((inst) => (
                <div
                  key={inst.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/identity/instances/${inst.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/identity/instances/${inst.id}`);
                    }
                  }}
                  className="flex items-center gap-3 py-2.5 cursor-pointer hover:bg-v2-muted/50 transition-colors px-2 -mx-2 rounded-v2-sm"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-v2-ink flex items-center gap-2">
                      {inst.name}
                      {inst.isProduction && <Pill tone="trust" size="sm">Production</Pill>}
                      {inst.hasOverride && <Pill tone="warning" size="sm">override</Pill>}
                    </div>
                    <div className="text-[11px] text-v2-ink-muted mt-0.5">
                      {inst.tier || 'CUSTOM'} · {inst.status || 'ACTIVE'}
                    </div>
                  </div>
                  <ChevronRight size={14} className="text-v2-ink-muted" aria-hidden="true" />
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Stage 3c-1 — tenant override slideout. add/remove modes. */}
      <TenantOverrideEditSlideout
        isOpen={editSlideout.open}
        mode={editSlideout.mode}
        tenantId={data.id}
        tenantName={data.name}
        onClose={() => setEditSlideout({ open: false, mode: 'add' })}
        onSaved={() => { window.location.reload(); }}
      />
    </V2PageShell>
  );
}

/* ────────────────────────────────────────────────────────────────
   DimensionCard — one card per identity-config setting. Shows the
   resolved value side-by-side with the Org default so a customer
   admin can see exactly what's been changed and what hasn't.
   ──────────────────────────────────────────────────────────────── */

function DimensionCard({ dimensionKey, label, cell, orgDefault }) {
  if (!cell) return null;
  const meta = DIMENSION_META[dimensionKey] || {};
  const Icon = meta.icon || Network;
  const isOverride = cell.inherit === false;
  const resolved = isOverride ? cell.value : orgDefault;

  return (
    <Card>
      <Card.Header
        title={<><Icon size={16} className={'inline -mt-0.5 mr-1.5 ' + (meta.iconTone || 'text-v2-action')} aria-hidden="true" />{label}</>}
        actions={
          isOverride
            ? <Pill tone="trust"  size="sm"><Pencil size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />Override</Pill>
            : <Pill tone="action" size="sm"><ArrowUp size={11} className="inline -mt-0.5 mr-1" aria-hidden="true" />Inherited</Pill>
        }
      />
      <Card.Body>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">
              {isOverride ? 'Override value' : 'Resolved value'}
            </div>
            <div className="text-[13.5px] text-v2-ink font-semibold">
              {renderValue(resolved)}
            </div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wide text-v2-ink-muted font-semibold mb-1">
              Organization default
            </div>
            <div className={'text-[13.5px] ' + (isOverride ? 'text-v2-ink-muted line-through' : 'text-v2-ink-secondary')}>
              {renderValue(orgDefault)}
            </div>
          </div>
        </div>
      </Card.Body>
      <Card.Footer>
        <Button variant="secondary" size="xs" disabled>
          {isOverride ? 'Remove override' : 'Add override'}
        </Button>
      </Card.Footer>
    </Card>
  );
}

/* Render a resolved value cleanly regardless of shape. The backend
 * payload mixes strings (svidTtl, rotation, trustDomain) and arrays
 * (compliance, federation). */
function renderValue(v) {
  if (v == null) return <span className="text-v2-ink-muted">—</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-v2-ink-muted">none</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {v.map((s) => <Pill key={String(s)} tone="muted" size="sm">{String(s)}</Pill>)}
      </div>
    );
  }
  return String(v);
}
